import React, { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, CheckCircle2, Loader2, Banknote, CreditCard, MapPin, StickyNote, PenSquare, Upload, X, Image as ImageIcon, Phone, Home, Building2, Map, Hash, Navigation } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';
import api from '../lib/api';
import LoginModal from '../components/LoginModal';

function DoneView({ done, navigate }) {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }, []);
  return (
    <div className="max-w-2xl mx-auto px-4 md:px-6 mt-16 pb-32 text-center">
      <div className="mx-auto w-20 h-20 rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center border border-emerald-500/30">
        <CheckCircle2 className="w-10 h-10" />
      </div>
      <h1 className="mt-6 text-3xl font-black text-white">¡Pedido realizado!</h1>
      <p className="mt-2 text-white/60">Número: <span className="text-orange-400 font-bold">{done.order_id}</span></p>
      <p className="mt-2 text-white/60">Total: <span className="text-white font-bold">${done.total?.toLocaleString('es-MX')}</span></p>
      <div className="mt-8 flex items-center justify-center gap-3">
        <Button onClick={() => navigate('/pedidos')} className="bg-orange-500 hover:bg-orange-600 text-black">Ver mis pedidos</Button>
        <Button onClick={() => navigate('/catalogo')} variant="outline" className="bg-transparent border-white/20 text-white hover:bg-white/10">Seguir comprando</Button>
      </div>
    </div>
  );
}

const buildFullAddress = (a) => {
  if (!a) return '';
  // Always prefer structured fields so ext/interior are included
  if (a.calle) {
    const parts = [
      `${a.calle} ${a.ext || ''}${a.interior ? ' int ' + a.interior : ''}`.trim(),
      a.colonia,
      a.cp ? `CP ${a.cp}` : null,
      a.ciudad,
      a.estado,
      a.referencia ? `Ref: ${a.referencia}` : null,
    ].filter(Boolean);
    return parts.join(', ');
  }
  if (a.full) return a.full;
  return '';
};

const CartPage = () => {
  const { cart, updateQty, removeItem, clear, refresh } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [done, setDone] = useState(null);
  const [openLogin, setOpenLogin] = useState(false);
  const [address, setAddress] = useState('');
  const [addressOverride, setAddressOverride] = useState(false);
  const [addressFields, setAddressFields] = useState({ calle: '', colonia: '', ciudad: '', estado: '', cp: '' });
  const setAF = (key, val) => setAddressFields(prev => ({ ...prev, [key]: val }));
  const addressFieldsFull = [addressFields.calle, addressFields.colonia, addressFields.ciudad, addressFields.estado, addressFields.cp].every(v => v.trim());
  const [notes, setNotes] = useState('');
  const [phonePrefix, setPhonePrefix] = useState('+52'); // default México
  const [phoneLocal, setPhoneLocal] = useState('');
  const contactPhone = `${phonePrefix} ${phoneLocal}`.trim();
  const [payment, setPayment] = useState('efectivo');
  const [proof, setProof] = useState(''); // base64 data URL
  const [proofName, setProofName] = useState('');
  const [placing, setPlacing] = useState(false);
  const [bank, setBank] = useState(null);
  const [couponInput, setCouponInput] = useState('');
  const [coupon, setCoupon] = useState(null);   // { code, discount_pct, discount_amount, new_total }
  const [couponError, setCouponError] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);

  // Descuento del cupón recalculado dinámicamente sobre el total actual del carrito
  const couponDisc = coupon ? Math.round(cart.total * (coupon.discount_pct / 100) * 100) / 100 : 0;
  const finalTotal = coupon ? Math.max(0, Math.round((cart.total - couponDisc) * 100) / 100) : cart.total;

  // Reset coupon when cart items change
  useEffect(() => { setCoupon(null); setCouponError(''); setCouponInput(''); }, [cart.items?.length]);

  const applyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    setCouponLoading(true); setCouponError('');
    try {
      const { data } = await api.post('/coupons/validate', { code, cart_total: cart.total });
      setCoupon(data);
    } catch (e) {
      setCoupon(null);
      setCouponError(e?.response?.data?.detail || 'Cupón inválido o expirado.');
    } finally { setCouponLoading(false); }
  };

  // Load bank/settings once for the transfer block
  useEffect(() => {
    let cancelled = false;
    api.get('/settings').then((r) => {
      if (cancelled) return;
      const d = r.data || {};
      setBank({
        name: d.bank_name || '',
        holder: d.bank_account_holder || '',
        clabe: d.bank_clabe || '',
        account: d.bank_account_number || '',
        note: d.bank_reference_note || '',
      });
    }).catch(() => setBank({ name: '', holder: '', clabe: '', account: '', note: '' }));
    return () => { cancelled = true; };
  }, []);

  const onProofChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast({ title: 'Archivo muy grande', description: 'Máximo 5 MB' }); return; }
    const reader = new FileReader();
    reader.onload = () => { setProof(reader.result); setProofName(file.name); };
    reader.readAsDataURL(file);
  };

  // Auto-fill address from saved profile address
  const savedAddress = user?.address && (user.address.calle || user.address.full) ? user.address : null;
  const savedFull = savedAddress ? buildFullAddress(savedAddress) : '';

  useEffect(() => {
    if (savedFull && !addressOverride && !address) {
      setAddress(savedFull);
    }
  }, [savedFull, addressOverride]);

  const checkout = async () => {
    if (!user) { setOpenLogin(true); return; }
    if (!cart.items.length) return;

    // Determine effective address
    let effectiveAddress = '';
    if (savedAddress && !addressOverride) {
      effectiveAddress = savedFull;
    } else {
      if (!addressFieldsFull) {
        toast({ title: 'Dirección incompleta', description: 'Completa todos los campos de dirección.' });
        return;
      }
      effectiveAddress = buildFullAddress(addressFields);
    }
    if (!effectiveAddress.trim()) { toast({ title: 'Dirección requerida' }); return; }

    const phoneDigits = (phoneLocal || '').replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      toast({ title: 'Teléfono requerido', description: 'Escribe un teléfono válido (mín. 10 dígitos) para que el repartidor te contacte.' });
      return;
    }
    setPlacing(true);
    try {
      const r = await api.post('/orders', { address: effectiveAddress, payment_method: payment, notes, contact_phone: contactPhone.trim(), coupon_code: coupon?.code || undefined });
      if (payment === 'mercadopago') {
        const pref = await api.post('/mercadopago/preference', { order_id: r.data.order_id });
        window.location.href = pref.data.init_point;
        return;
      }
      setDone(r.data);
      refresh();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'No se pudo crear el pedido' });
    } finally { setPlacing(false); }
  };

  if (done) {
    return (
      <DoneView done={done} navigate={navigate} />
    );
  }

  // ── Shared field styles ──────────────────────────────────────────────────
  const inputBase = "w-full h-11 rounded-xl bg-neutral-800/60 border border-white/[0.09] text-white text-[14px] px-3.5 outline-none placeholder:text-white/25 hover:border-white/20 focus:border-orange-500/60 focus:bg-neutral-800 focus:ring-2 focus:ring-orange-500/15 transition-all";
  const inputIcon = "w-full h-11 rounded-xl bg-neutral-800/60 border border-white/[0.09] text-white text-[14px] pl-9 pr-3.5 outline-none placeholder:text-white/25 hover:border-white/20 focus:border-orange-500/60 focus:bg-neutral-800 focus:ring-2 focus:ring-orange-500/15 transition-all";
  const inputIconSm = "w-full h-11 rounded-xl bg-neutral-800/60 border border-white/[0.09] text-white text-[13px] pl-8 pr-2 outline-none placeholder:text-white/25 hover:border-white/20 focus:border-orange-500/60 focus:bg-neutral-800 focus:ring-2 focus:ring-orange-500/15 transition-all";
  const fieldCard = "bg-neutral-900 border border-white/[0.08] rounded-2xl p-5";

  // ── Address fields block (reused in aside) ───────────────────────────────
  const addressBlock = savedAddress && !addressOverride ? (
    <div className="rounded-xl border border-orange-500/25 bg-orange-500/[0.06] p-3.5" data-testid="cart-saved-address">
      <div className="flex items-center gap-1.5 mb-1">
        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
        <span className="text-[10px] font-black uppercase tracking-wider text-emerald-400">Guardada · {savedAddress.alias || 'Casa'}</span>
      </div>
      <p className="text-white text-[14px] leading-snug">{savedFull}</p>
      <p className="text-white/45 text-xs mt-1">Se usará automáticamente para este pedido.</p>
      <div className="flex items-center gap-2 mt-3">
        <button type="button"
          onClick={() => { setAddressOverride(true); setAddress(''); setAddressFields({ calle: '', colonia: '', ciudad: '', estado: '', cp: '' }); }}
          className="text-xs font-bold text-orange-400 hover:text-orange-300 inline-flex items-center gap-1"
          data-testid="cart-use-other-address-btn">
          <PenSquare className="w-3 h-3" /> Usar otra dirección
        </button>
        <span className="text-white/20">·</span>
        <Link to="/perfil?tab=direccion" className="text-xs font-bold text-white/60 hover:text-white">Editar</Link>
      </div>
    </div>
  ) : (
    <div data-testid="cart-address-fields" className="space-y-3">
      {/* Calle */}
      <div>
        <label className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold text-white/50 mb-1.5">
          <Home className="w-3 h-3" /> Calle y número <span className="text-orange-500">*</span>
        </label>
        <div className="relative">
          <Home className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/25 pointer-events-none" />
          <input value={addressFields.calle} onChange={(e) => setAF('calle', e.target.value)}
            placeholder="Ej. Av. Revolución 245" data-testid="addr-calle" className={inputIcon} />
        </div>
      </div>
      {/* Colonia */}
      <div>
        <label className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold text-white/50 mb-1.5">
          <Navigation className="w-3 h-3" /> Colonia <span className="text-orange-500">*</span>
        </label>
        <div className="relative">
          <Navigation className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/25 pointer-events-none" />
          <input value={addressFields.colonia} onChange={(e) => setAF('colonia', e.target.value)}
            placeholder="Ej. Mitras Poniente" data-testid="addr-colonia" className={inputIcon} />
        </div>
      </div>
      {/* Ciudad / Estado / CP — 3 cols en sm+ */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold text-white/50 mb-1.5">
            <Building2 className="w-3 h-3" /> Ciudad <span className="text-orange-500">*</span>
          </label>
          <div className="relative">
            <Building2 className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/25 pointer-events-none" />
            <input value={addressFields.ciudad} onChange={(e) => setAF('ciudad', e.target.value)}
              placeholder="Monterrey" data-testid="addr-ciudad" className={inputIconSm} />
          </div>
        </div>
        <div>
          <label className="flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold text-white/50 mb-1.5">
            <Map className="w-3 h-3" /> Estado <span className="text-orange-500">*</span>
          </label>
          <div className="relative">
            <Map className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/25 pointer-events-none" />
            <input value={addressFields.estado} onChange={(e) => setAF('estado', e.target.value)}
              placeholder="Nuevo León" data-testid="addr-estado" className={inputIconSm} />
          </div>
        </div>
        <div>
          <label className="flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold text-white/50 mb-1.5">
            <Hash className="w-3 h-3" /> C.P. <span className="text-orange-500">*</span>
          </label>
          <div className="relative">
            <Hash className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/25 pointer-events-none" />
            <input value={addressFields.cp} onChange={(e) => setAF('cp', e.target.value)}
              placeholder="64650" maxLength={10} data-testid="addr-cp" className={inputIconSm} />
          </div>
        </div>
      </div>
      {savedAddress && addressOverride && (
        <button type="button"
          onClick={() => { setAddressOverride(false); setAddressFields({ calle: '', colonia: '', ciudad: '', estado: '', cp: '' }); }}
          className="text-xs font-bold text-orange-400 hover:text-orange-300">
          ← Usar mi dirección guardada
        </button>
      )}
      {!savedAddress && (
        <p className="text-xs text-white/40">
          Tip: <Link to="/perfil?tab=direccion" className="text-orange-400 hover:text-orange-300 underline">guarda tu dirección</Link> para que se autocomplete en futuros pedidos.
        </p>
      )}
    </div>
  );

  return (
    <div className="max-w-[1200px] mx-auto px-4 md:px-6 mt-6 pb-6">
      <h1 className="text-3xl md:text-4xl font-black text-white mb-6">Tu carrito</h1>

      {cart.items.length === 0 ? (
        <div className="text-center py-20 bg-neutral-900 border border-white/[0.08] rounded-2xl">
          <ShoppingBag className="w-10 h-10 mx-auto text-orange-500" />
          <p className="mt-4 font-semibold text-white">Tu carrito está vacío</p>
          <p className="text-sm text-white/50 mt-1">Explora el catálogo y encuentra tu vape favorito.</p>
          <Link to="/catalogo" className="inline-block mt-6">
            <Button className="bg-orange-500 hover:bg-orange-600 text-black font-bold">Ir al catálogo</Button>
          </Link>
        </div>
      ) : (
        <>
          {/*
            Layout strategy:
            • Mobile  → flex-col: summary(1) → products(2) → form(3)
            • Desktop → CSS grid col-start/row-start: products(col1-2 row1) | summary(col3 row1 sticky) | form(col3 row2)
          */}
          <div className="flex flex-col gap-5 lg:grid lg:grid-cols-3 lg:items-start lg:gap-8">

            {/* ① SUMMARY ─ mobile: first / desktop: col 3 row 1 sticky */}
            <aside className="lg:col-start-3 lg:row-start-1 lg:sticky lg:top-24 bg-neutral-900 border border-white/[0.08] rounded-2xl p-5 shadow-2xl space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-black text-xl text-white tracking-tight">Resumen</h3>
                <span className="text-[11px] uppercase tracking-wider font-bold text-orange-400 bg-orange-500/10 border border-orange-500/25 px-2.5 py-1 rounded-full">
                  {cart.items.length} {cart.items.length === 1 ? 'artículo' : 'artículos'}
                </span>
              </div>

              <div className="space-y-2.5 text-sm">
                <div className="flex justify-between text-white/70">
                  <span>Subtotal</span>
                  <span className="text-white font-semibold">${cart.subtotal?.toLocaleString('es-MX')}</span>
                </div>
                {cart.discount > 0 && (
                  <div className="flex justify-between text-emerald-400" data-testid="cart-discount">
                    <span>Ahorro (bulk)</span><span className="font-semibold">− ${cart.discount?.toLocaleString('es-MX')}</span>
                  </div>
                )}
                {cart.promo_applied && cart.promo_discount > 0 && (
                  <div className="flex justify-between text-orange-400" data-testid="cart-promo-discount">
                    <span>{cart.promo_discount_type === 'fixed_mxn' ? 'Promo fija' : `Promo ${cart.promo_pct}%`}</span>
                    <span className="font-semibold">− ${cart.promo_discount?.toLocaleString('es-MX')}</span>
                  </div>
                )}
                <div className="flex justify-between text-white/70">
                  <span>Envío</span>
                  <span>{cart.shipping === 0
                    ? <span className="text-emerald-400 font-bold">Gratis</span>
                    : <span className="text-white font-semibold">${cart.shipping?.toLocaleString('es-MX')}</span>}
                  </span>
                </div>
                <div className="h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
                {/* Coupon */}
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input value={couponInput}
                      onChange={(e) => { setCouponInput(e.target.value.toUpperCase()); setCouponError(''); if (coupon) setCoupon(null); }}
                      onKeyDown={(e) => e.key === 'Enter' && applyCoupon()}
                      placeholder="CÓDIGO DE CUPÓN" maxLength={20}
                      className="flex-1 h-10 px-3 rounded-xl bg-white/5 border border-white/[0.09] text-white text-sm font-mono tracking-widest placeholder:text-white/25 focus:outline-none focus:border-orange-500/60 transition-colors"
                      data-testid="coupon-input" />
                    <button onClick={applyCoupon} disabled={couponLoading || !couponInput.trim()}
                      className="h-10 px-4 rounded-xl bg-orange-500 hover:bg-orange-600 text-black font-bold text-xs disabled:opacity-40 transition-colors flex items-center gap-1.5"
                      data-testid="coupon-apply-btn">
                      {couponLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Aplicar'}
                    </button>
                  </div>
                  {couponError && <p className="text-rose-400 text-xs" data-testid="coupon-error">{couponError}</p>}
                  {coupon && (
                    <div className="flex justify-between items-center text-emerald-400 text-sm bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-3 py-2" data-testid="coupon-applied">
                      <span className="font-bold">{coupon.code} — {coupon.discount_pct}% off</span>
                      <span className="font-black">− ${couponDisc?.toLocaleString('es-MX')}</span>
                    </div>
                  )}
                </div>
                <div className="h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
                <div className="flex justify-between items-baseline pt-1">
                  <span className="text-white/80 text-sm uppercase tracking-wider font-bold">Total</span>
                  <span className="text-3xl font-black text-white">${finalTotal?.toLocaleString('es-MX')}</span>
                </div>
              </div>

              {/* Free shipping bar */}
              {cart.subtotal > 0 && (() => {
                const threshold = cart.free_shipping_threshold || 1500;
                const unlocked = cart.shipping === 0;
                return (
                  <div className={`rounded-xl p-3 border ${unlocked ? 'bg-emerald-500/10 border-emerald-500/25' : 'bg-orange-500/10 border-orange-500/25'}`}>
                    <div className={`flex items-center justify-between text-[11px] font-semibold mb-1.5 ${unlocked ? 'text-emerald-400' : 'text-orange-300'}`}>
                      <span>Envío gratis</span>
                      <span>{unlocked ? '¡Desbloqueado!' : `$${(threshold - cart.subtotal).toLocaleString('es-MX')} más`}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-black/40 overflow-hidden">
                      <div className={`h-full transition-all ${unlocked ? 'bg-gradient-to-r from-emerald-400 to-emerald-600' : 'bg-gradient-to-r from-orange-400 to-orange-600'}`}
                        style={{ width: unlocked ? '100%' : `${Math.min(99, (cart.subtotal / threshold) * 100)}%` }} />
                    </div>
                  </div>
                );
              })()}
            </aside>

            {/* ② PRODUCTS ─ mobile: second / desktop: col 1-2 row 1 */}
            <div className="lg:col-span-2 lg:col-start-1 lg:row-start-1 space-y-3">
              {cart.promo_applied && (
                <div data-testid="cart-promo-banner"
                  className="flex items-start gap-3 rounded-2xl border border-orange-500/30 bg-orange-500/10 px-4 py-3.5 animate-in fade-in slide-in-from-top-2 duration-300">
                  <span className="text-2xl leading-none mt-0.5">🎉</span>
                  <div>
                    <p className="text-orange-300 font-black text-sm">¡Promoción activada!</p>
                    <p className="text-white/80 text-sm mt-0.5">
                      {cart.promo_discount_type === 'fixed_mxn'
                        ? <><span className="text-orange-400 font-black">${cart.promo_discount?.toLocaleString('es-MX')} MXN de descuento</span> aplicados a tu pedido.</>
                        : <>Compraste {cart.promo_qty} vapes y obtuviste un{' '}<span className="text-orange-400 font-black">{cart.promo_pct}% de descuento</span>.</>
                      }
                    </p>
                  </div>
                </div>
              )}
              {cart.items.map((it) => (
                <div key={it.product_id} className="bg-neutral-900 rounded-2xl border border-white/[0.08] p-4 flex items-center gap-4">
                  <div className="w-20 h-20 rounded-xl overflow-hidden bg-neutral-800 shrink-0">
                    {it.image && <img src={it.image} alt={it.title} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-orange-400 uppercase tracking-wider">{it.vendor}</p>
                    <h4 className="font-semibold text-white truncate uppercase">{it.title}</h4>
                    {it.line_discount > 0 ? (
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <p className="text-lg font-black text-white">${it.line_total?.toLocaleString('es-MX')}</p>
                        <p className="text-xs text-white/40 line-through">${it.original_line_total?.toLocaleString('es-MX')}</p>
                        {it.applied_tier && (
                          <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-orange-500/15 text-orange-400 border border-orange-500/30" data-testid="cart-line-tier">
                            Promo {it.applied_tier.qty}x ${Number(it.applied_tier.price).toLocaleString('es-MX', { minimumFractionDigits: 0 })}
                          </span>
                        )}
                      </div>
                    ) : (
                      <p className="text-lg font-black text-white">${it.price?.toLocaleString('es-MX')}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 bg-white/5 rounded-full px-1 h-10 border border-white/[0.09]">
                    <button onClick={() => updateQty(it.product_id, it.qty - 1)} className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center text-white">
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="font-bold w-6 text-center text-white">{it.qty}</span>
                    <button onClick={() => updateQty(it.product_id, it.qty + 1)} disabled={it.stock != null && it.qty >= it.stock}
                      className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center text-white disabled:opacity-40 disabled:cursor-not-allowed">
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  <button onClick={() => removeItem(it.product_id)} className="text-white/40 hover:text-rose-400 transition">
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>

            {/* ③ FORM ─ mobile: third / desktop: col 3 row 2 */}
            <div className="lg:col-start-3 lg:row-start-2 pb-24 lg:pb-0 space-y-4">
              {user ? (
                <>
                  {/* Address card */}
                  <div className={fieldCard}>
                    <h4 className="flex items-center gap-2 text-[13px] font-black text-white mb-4">
                      <MapPin className="w-4 h-4 text-orange-400" /> Dirección de entrega
                    </h4>
                    {addressBlock}
                  </div>

                  {/* Phone card */}
                  <div className={`${fieldCard} space-y-3`}>
                    <label className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold text-white/50">
                      <Phone className="w-3.5 h-3.5" /> Teléfono de contacto <span className="text-orange-500">*</span>
                    </label>
                    <div className="flex gap-2">
                      <div className="relative shrink-0">
                        <select value={phonePrefix} onChange={(e) => setPhonePrefix(e.target.value)}
                          data-testid="cart-phone-prefix-select" aria-label="Prefijo de país"
                          className="appearance-none h-11 pl-3 pr-8 rounded-xl bg-neutral-800/60 border border-white/[0.09] text-white text-[14px] outline-none hover:border-white/20 focus:border-orange-500/60 focus:ring-2 focus:ring-orange-500/15 transition-all font-mono font-bold cursor-pointer">
                          <option value="+52">🇲🇽 +52</option>
                          <option value="+1">🇺🇸 +1</option>
                          <option value="+34">🇪🇸 +34</option>
                          <option value="+54">🇦🇷 +54</option>
                          <option value="+55">🇧🇷 +55</option>
                          <option value="+56">🇨🇱 +56</option>
                          <option value="+57">🇨🇴 +57</option>
                          <option value="+51">🇵🇪 +51</option>
                          <option value="+58">🇻🇪 +58</option>
                          <option value="+502">🇬🇹 +502</option>
                          <option value="+503">🇸🇻 +503</option>
                          <option value="+504">🇭🇳 +504</option>
                          <option value="+505">🇳🇮 +505</option>
                          <option value="+506">🇨🇷 +506</option>
                          <option value="+507">🇵🇦 +507</option>
                          <option value="+591">🇧🇴 +591</option>
                          <option value="+593">🇪🇨 +593</option>
                          <option value="+595">🇵🇾 +595</option>
                          <option value="+598">🇺🇾 +598</option>
                        </select>
                        <svg className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/60" viewBox="0 0 20 20" fill="currentColor"><path d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.24 4.38a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" /></svg>
                      </div>
                      <input value={phoneLocal}
                        onChange={(e) => setPhoneLocal(e.target.value.replace(/[^\d\s()-]/g, '').slice(0, 20))}
                        placeholder="81 1234 5678" type="tel" inputMode="tel" autoComplete="tel-national" required
                        data-testid="cart-contact-phone-input" className={`flex-1 min-w-0 ${inputBase}`} />
                    </div>
                    <p className="text-[11px] text-white/45 leading-snug">
                      Lo usaremos <strong className="text-white/70">sólo para este pedido</strong> si el repartidor necesita contactarte.
                    </p>
                  </div>

                  {/* Notes card */}
                  <div className={`${fieldCard} space-y-3`}>
                    <label className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold text-white/50">
                      <StickyNote className="w-3.5 h-3.5" /> Notas para el repartidor
                    </label>
                    <textarea value={notes} onChange={(e) => setNotes(e.target.value.slice(0, 400))}
                      placeholder="Casa blanca con reja negra, timbre no sirve — mándame WhatsApp al llegar..."
                      rows={3} data-testid="cart-notes-input"
                      className="w-full rounded-xl bg-neutral-800/60 border border-white/[0.09] text-white text-[14px] px-3.5 py-2.5 outline-none placeholder:text-white/25 hover:border-white/20 focus:border-orange-500/60 focus:bg-neutral-800 focus:ring-2 focus:ring-orange-500/15 transition-all resize-none leading-snug" />
                    <div className="flex justify-end">
                      <span className="text-[10px] text-white/30 tabular-nums">{notes.length}/400</span>
                    </div>
                  </div>

                  {/* Payment card */}
                  <div className={`${fieldCard} space-y-3`}>
                    <label className="block text-[11px] uppercase tracking-wider font-bold text-white/50">Método de pago</label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { id: 'efectivo', label: 'Efectivo', Icon: Banknote },
                        { id: 'mercadopago', label: 'Mercado Pago', Icon: CreditCard },
                      ].map(({ id, label, Icon }) => {
                        const active = payment === id;
                        return (
                          <button key={id} type="button" onClick={() => setPayment(id)}
                            data-testid={`payment-${id}-btn`}
                            className={`flex flex-col items-center gap-1.5 rounded-xl border p-3 transition-all ${
                              active
                                ? 'border-orange-500 bg-orange-500/10 text-orange-300 shadow-[0_0_0_1px_rgba(249,115,22,0.35)]'
                                : 'border-white/[0.09] bg-neutral-800/40 text-white/60 hover:border-white/25 hover:text-white'
                            }`}>
                            <Icon className={`w-5 h-5 ${active ? 'text-orange-400' : ''}`} />
                            <span className="text-[11px] font-bold tracking-wide">{label}</span>
                          </button>
                        );
                      })}
                    </div>
                    {payment === 'mercadopago' && (
                      <div className="rounded-xl border border-[#009EE3]/30 bg-[#009EE3]/[0.06] p-4 space-y-3 animate-in fade-in slide-in-from-top-1 duration-300" data-testid="mercadopago-info-block">
                        <div className="flex items-start gap-2.5">
                          <CreditCard className="w-4 h-4 text-[#009EE3] mt-0.5 shrink-0" />
                          <div>
                            <p className="text-white text-[13px] font-bold">Paga con Mercado Pago</p>
                            <p className="text-white/60 text-xs mt-1 leading-snug">Serás redirigido a Mercado Pago para pagar con tarjeta, OXXO, SPEI u otros métodos.</p>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {['Visa', 'Mastercard', 'OXXO', 'SPEI', 'Amex'].map((m) => (
                            <span key={m} className="px-2 py-0.5 rounded-full bg-white/10 border border-white/10 text-[10px] font-bold text-white/60">{m}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Desktop CTA */}
                  <div className="hidden lg:block space-y-3 pt-1">
                    <Button onClick={checkout} disabled={placing} data-testid="checkout-btn"
                      className="w-full h-12 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-black font-black text-[15px] shadow-lg shadow-orange-500/20 transition-all">
                      {placing
                        ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{payment === 'mercadopago' ? 'Redirigiendo...' : 'Procesando...'}</>
                        : (payment === 'mercadopago' ? 'Pagar con Mercado Pago' : 'Finalizar pedido')
                      }
                    </Button>
                    <button onClick={clear} data-testid="clear-cart-btn"
                      className="w-full text-xs text-white/40 hover:text-rose-400 uppercase tracking-wider font-semibold transition-colors">
                      Vaciar carrito
                    </button>
                  </div>
                </>
              ) : (
                <div className={`${fieldCard} text-center`}>
                  <p className="text-sm text-white/70">Inicia sesión para completar tu pedido.</p>
                </div>
              )}
            </div>

          </div>

          {/* ── Mobile fixed bottom CTA ── */}
          <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-neutral-950/95 backdrop-blur-md border-t border-white/[0.08] px-4 py-3 safe-area-inset-bottom">
            <Button onClick={checkout} disabled={placing} data-testid="checkout-btn-mobile"
              className="w-full h-12 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-black font-black text-[15px] shadow-lg shadow-orange-500/20 transition-all">
              {placing
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{payment === 'mercadopago' ? 'Redirigiendo...' : 'Procesando...'}</>
                : (user
                    ? (payment === 'mercadopago' ? 'Pagar con Mercado Pago' : 'Finalizar pedido')
                    : 'Iniciar sesión para continuar')
              }
            </Button>
          </div>
        </>
      )}
      <LoginModal open={openLogin} onOpenChange={setOpenLogin} />
    </div>
  );
};

export default CartPage;
